import { useTranslation } from "@next-platform/client-common-core";
import parseRulebookImport from "@next-platform/client-common-core/rulebook/editor/parseRulebookImport";
import { getRulebookDraft, saveRulebookDraft } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import { Button, ButtonVariant, Modal, TextArea } from "@next-platform/client-common-spicy-ui";
import type { RulebookDefinition } from "@next-platform/packages-ts-types";
import React, { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";

type RulebookImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

// eslint-disable-next-line no-magic-numbers -- semantic constant definition; the named constant IS the abstraction this rule targets.
const IMPORT_MAX_BYTES = 2 * 1024 * 1024;
const FILE_TOO_LARGE_ERROR = "File too large";
const COULD_NOT_SAVE_ERROR = "Could not save draft";

type OverwritePromptProps = {
  onConfirm: () => void;
};

const OverwritePrompt = ({ onConfirm }: OverwritePromptProps) => (
  <div className="flex flex-col gap-2" data-testid="RulebookImportModal-Overwrite">
    <span>
      <Trans i18nKey="rulebookEditor:modal.overwritePrompt" />
    </span>
    <Button variant={ButtonVariant.CONTAINED} onClick={onConfirm} data-testid="RulebookImportModal-OverwriteConfirm">
      <Trans i18nKey="rulebookEditor:modal.overwriteConfirm" />
    </Button>
  </div>
);

const RulebookImportModal = ({ isOpen, onClose }: RulebookImportModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stateText, setStateText] = useState("");
  const [stateError, setStateError] = useState("");
  const [statePending, setStatePending] = useState<RulebookDefinition | null>(null);

  const reset = useCallback(() => {
    setStateText("");
    setStateError("");
    setStatePending(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleTextChange = useCallback((value: string) => setStateText(value), []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > IMPORT_MAX_BYTES) {
      setStateError(FILE_TOO_LARGE_ERROR);
      return;
    }
    void file
      .text()
      .then(setStateText)
      .catch(() => setStateError("Could not read file"));
  }, []);

  const finishImport = useCallback(
    (definition: RulebookDefinition) => {
      const draft = saveRulebookDraft({ definition });
      if (!draft) {
        setStateError(COULD_NOT_SAVE_ERROR);
        return;
      }
      reset();
      onClose();
      navigate(`${ROUTES.rulebookEditor}/${definition.id}`);
    },
    [reset, onClose, navigate],
  );

  const runImport = useCallback(
    (text: string) => {
      if (text.length > IMPORT_MAX_BYTES) {
        setStateError(FILE_TOO_LARGE_ERROR);
        setStatePending(null);
        return;
      }
      const result = parseRulebookImport({ text });
      if ("error" in result) {
        setStateError(result.error);
        setStatePending(null);
        return;
      }
      setStateError("");
      if (getRulebookDraft({ id: result.definition.id })) {
        setStatePending(result.definition);
        return;
      }
      finishImport(result.definition);
    },
    [finishImport],
  );

  const handleSubmit = useCallback(() => runImport(stateText), [runImport, stateText]);
  const handleOverwrite = useCallback(() => {
    if (statePending) {
      finishImport(statePending);
    }
  }, [statePending, finishImport]);

  return (
    <Modal title={t("rulebookEditor:modal.importHeading")} open={isOpen} onOpenChange={handleClose} data-testid="RulebookImportModal">
      <div className="flex flex-col gap-4">
        <input type="file" accept=".json,application/json" onChange={handleFileChange} data-testid="RulebookImportModal-FileInput" />
        <TextArea
          placeholder={t("rulebookEditor:modal.importPaste")}
          value={stateText}
          onChange={handleTextChange}
          data-testid="RulebookImportModal-TextArea"
        />
        {stateError ? (
          <div data-testid="RulebookImportModal-Error" className="text-red-9">
            {stateError}
          </div>
        ) : null}
        {statePending ? <OverwritePrompt onConfirm={handleOverwrite} /> : null}
        <div className="flex justify-end gap-2">
          <Button variant={ButtonVariant.OUTLINED} onClick={handleClose} data-testid="RulebookImportModal-Cancel">
            <Trans i18nKey="rulebookEditor:modal.cancel" />
          </Button>
          <Button variant={ButtonVariant.CONTAINED} onClick={handleSubmit} disabled={!stateText.trim()} data-testid="RulebookImportModal-Submit">
            <Trans i18nKey="rulebookEditor:modal.importSubmit" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default memo(RulebookImportModal);
